import { useState } from "react";
import { ChevronDown, ChevronUp, Edit, Trash2 } from "lucide-react";
import { Preference, TimeFrame } from "../AddNewDoctor";

interface PreferencesAccordionProps {
  preferences: Preference[];
  onEdit: (preference: Preference) => void;
  onDelete: (preferenceId: string) => void;
}

const DAY_LABELS: Record<string, string> = {
  sunday: "Sun",
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
};

const TYPE_COLORS: Record<string, string> = {
  Online: "bg-blue-100 text-blue-800",
  "Dandar Clinic": "bg-purple-100 text-purple-800",
  "Self Clinic": "bg-green-100 text-green-800",
  "Instent Appointment": "bg-orange-100 text-orange-800",
};

export const PreferencesAccordion = ({
  preferences,
  onEdit,
  onDelete,
}: PreferencesAccordionProps) => {
  const [expandedPreference, setExpandedPreference] = useState<string | null>(
    null,
  );

  const togglePreference = (preferenceId: string) => {
    setExpandedPreference(
      expandedPreference === preferenceId ? null : preferenceId,
    );
  };

  const groupPreferencesByType = () => {
    const grouped: Record<string, Preference[]> = {};
    preferences.forEach((pref) => {
      if (!grouped[pref.type]) {
        grouped[pref.type] = [];
      }
      grouped[pref.type].push(pref);
    });
    return grouped;
  };

  const groupedPreferences = groupPreferencesByType();

  const formatTime = (time: string) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="space-y-4">
      {Object.entries(groupedPreferences).map(([type, typePreferences]) => (
        <div key={type} className="border border-gray-200 rounded-lg">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${TYPE_COLORS[type]}`}
                >
                  {type}
                </span>
                <span className="text-sm text-gray-600">
                  {typePreferences.length}{" "}
                  {typePreferences.length === 1 ? "preference" : "preferences"}
                </span>
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {typePreferences.map((preference) => (
              <div key={preference.id} className="bg-white">
                <button
                  onClick={() => togglePreference(preference.id)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4 text-left">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {preference.clinicDetails?.name || `${type} Schedule`}
                        {preference.weekOfMonth && (
                          <span className="ml-2 text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                            Week {preference.weekOfMonth}
                          </span>
                        )}
                      </h4>
                      {preference.clinicDetails?.address && (
                        <p className="text-sm text-gray-600">
                          {preference.clinicDetails.address},{" "}
                          {preference.clinicDetails.city}
                        </p>
                      )}
                      <p className="text-sm text-gray-600">
                        Consultation Fee: ₹{preference.consultationFee}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(preference);
                        }}
                        className="p-1 text-blue-600 hover:text-blue-800"
                        title="Edit preference"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(preference.id);
                        }}
                        className="p-1 text-red-600 hover:text-red-800"
                        title="Delete preference"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    {expandedPreference === preference.id ? (
                      <ChevronUp size={20} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>
                </button>

                {expandedPreference === preference.id && (
                  <div className="px-4 pb-4">
                    {/* Clinic Details */}
                    {(preference.type === "Self Clinic" ||
                      preference.type === "Dandar Clinic") && (
                      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                        <h5 className="font-medium text-gray-900 mb-2">
                          Clinic Details
                        </h5>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-gray-600">Name:</span>{" "}
                            <span className="font-medium">
                              {preference.clinicDetails.name}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Address:</span>{" "}
                            <span className="font-medium">
                              {preference.clinicDetails.address}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">City:</span>{" "}
                            <span className="font-medium">
                              {preference.clinicDetails.city}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">State:</span>{" "}
                            <span className="font-medium">
                              {preference.clinicDetails.state}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Pincode:</span>{" "}
                            <span className="font-medium">
                              {preference.clinicDetails.pincode}
                            </span>
                          </div>
                          {preference.clinicDetails.mobile_number && (
                            <div>
                              <span className="text-gray-600">Mobile:</span>{" "}
                              <span className="font-medium">
                                {preference.clinicDetails.mobile_number}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Schedule */}
                    <div>
                      <h5 className="font-medium text-gray-900 mb-3">
                        Schedule
                      </h5>
                      <div className="space-y-3">
                        {Object.entries(preference.enabledDays)
                          .filter(([_, enabled]) => enabled)
                          .map(([day]) => {
                            const daySlots = preference.timeFrames.filter(
                              (slot: TimeFrame) => slot.day_of_week === day,
                            );
                            return (
                              <div key={day} className="flex items-start gap-4">
                                <div className="w-16 flex-shrink-0">
                                  <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm font-medium">
                                    {DAY_LABELS[day]}
                                  </span>
                                </div>
                                <div className="flex-1">
                                  {daySlots.length > 0 ? (
                                    <div className="space-y-2">
                                      {daySlots.map((slot: TimeFrame) => (
                                        <div
                                          key={slot.id}
                                          className="flex items-center gap-3 px-3 py-2 bg-green-50 rounded border border-green-100"
                                        >
                                          <span className="text-sm font-medium">
                                            {formatTime(slot.start_time)} -{" "}
                                            {formatTime(slot.end_time)}
                                          </span>
                                          <span className="text-xs text-gray-600 bg-white px-2 py-1 rounded">
                                            {slot.slot_duration} min slots
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-sm text-gray-500 italic">
                                      No slots configured
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {preferences.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No preferences added yet. Add your first preference above.
        </div>
      )}
    </div>
  );
};
